import React from 'react';
import { periodEndDate } from '@/api/handlers/resources/salaries';
import {
    useCurrentTaxSettingsQuery,
    useTaxSettingsOnDateQuery,
} from '@/api/queries';
import { rateTextOf, type RateText } from './salariesMeta';

/**
 * The rates the salaries screen quotes.
 *
 * BOS-109 moved the payroll arithmetic server-side, where the rates come from
 * the effective-dated `TaxRateSet` — resolved on the **period's end date** for a
 * run, and on **today** for the forward-looking register figures. The screen
 * reads the same sets from the same endpoint so a column header cannot caption
 * someone else's arithmetic: a June run relabels itself if June's rates differed
 * from today's.
 *
 * Both hooks answer `null` rather than a fallback when no set is in force. That
 * is a real state, not an error — the register still lists employees and still
 * reports a zero employer share when no rates govern them — and a quoted rate
 * that did not produce the figure beside it is worse than no rate at all. The
 * labels drop the percentage; the figures stay.
 */

/** The rates in force **today** — what the register's monthly figures use. */
export const useCurrentSalaryRates = (enabled = true): RateText | null => {
    const query = useCurrentTaxSettingsQuery(enabled);
    return React.useMemo(
        () =>
            rateTextOf(query.data?.success ? query.data.response.rates : null),
        [query.data],
    );
};

/**
 * The rates in force on a **period's end date** — what that period's run
 * applied, and what `NO_RATES_IN_FORCE` refuses on when there are none.
 */
export const useSalaryRatesForPeriod = (
    period: string | null,
): RateText | null => {
    const date = period ? periodEndDate(period) : null;
    const query = useTaxSettingsOnDateQuery(date);
    return React.useMemo(
        () =>
            rateTextOf(query.data?.success ? query.data.response.rates : null),
        [query.data],
    );
};
